import type { BehaviorDefinition, BehaviorSetting, BehaviorSettings } from '../index.js'

export function sameSetting(a: BehaviorSetting | undefined, b: BehaviorSetting | undefined): boolean {
  return a !== undefined && b !== undefined && a.enabled === b.enabled && a.weight === b.weight && a.best === b.best && a.worst === b.worst
}

export function sameSettings(a: BehaviorSettings, b: BehaviorSettings, behaviors: readonly BehaviorDefinition[]): boolean {
  return behaviors.every(({ key }) => sameSetting(a[key], b[key]))
}

/** The entries of a facility's settings that differ from the master - which is exactly what an override is. */
export function overridesFrom(settings: BehaviorSettings, master: BehaviorSettings, behaviors: readonly BehaviorDefinition[]): BehaviorSettings {
  return Object.fromEntries(
    behaviors.flatMap(({ key }): [string, BehaviorSetting][] => {
      const setting = settings[key]
      return setting && !sameSetting(setting, master[key]) ? [[key, setting]] : []
    }),
  )
}

/** The combined weight of the behaviors that are on. Weights of behaviors that are off count for nothing. */
export function enabledWeightTotal(settings: BehaviorSettings, behaviors: readonly BehaviorDefinition[]): number {
  const total = behaviors.reduce((sum, { key }) => sum + (settings[key]?.enabled ? settings[key].weight : 0), 0)
  // Avoids 0.1 + 0.2 style noise in the displayed total.
  return Math.round(total * 100) / 100
}

/** Whether a behavior's anchors are rates (shown as percentages) or counts: every standard behavior and a per-shift custom one are rates. */
export function anchorsAreRates(behavior: Pick<BehaviorDefinition, 'basis'>): boolean {
  return behavior.basis === null || behavior.basis === 'per_shift'
}

/** A behavior scored against a minimum per period has one level, not two: the minimum is its `best`, and its `worst` is not read. */
export function hasMinimumOnly(behavior: Pick<BehaviorDefinition, 'basis'>): boolean {
  return behavior.basis === 'per_period_minimum'
}

/** True when a minimum per period is not a whole number of at least one, which the API refuses. */
export function minimumIsInvalid(setting: Pick<BehaviorSetting, 'best'>): boolean {
  return !(Number.isInteger(setting.best) && setting.best >= 1)
}

/** True when the anchors point the wrong way for the behavior's direction, which the API refuses. */
export function anchorsDisagree(behavior: Pick<BehaviorDefinition, 'direction'>, setting: Pick<BehaviorSetting, 'best' | 'worst'>): boolean {
  return behavior.direction === 'positive' ? setting.best <= setting.worst : setting.best >= setting.worst
}

/** A key suggested from a label: lowercase words joined by underscores, within the API's rules. */
export function keyFromLabel(label: string): string {
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'b_$1')
  return key.slice(0, 40)
}

export const BEHAVIOR_KEY_PATTERN = /^[a-z][a-z0-9_]{2,39}$/
